import React, { useState, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { getSubscriptionStatus, checkTrialStatus, createCheckoutSession, type SubscriptionStatus } from '../lib/billing'
import { TrialExpiredModal } from './TrialExpiredModal'

interface ProtectedRouteProps {
  children: React.ReactNode
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-2 text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    // Redirect to login page with return url
    return <Navigate to="/auth/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}

interface TeamProtectedRouteProps {
  children: React.ReactNode
  teamId: string
  // 'admin' -- admin only (team governance; matches BillingManagement.tsx /
  // TeamManagement.tsx's own admin-only reasoning). 'manager' -- admin OR
  // manager, matching the admin-or-manager pattern used throughout the app
  // for pod-scoped visibility (AgentsCostPanel.tsx, NewDashboard.tsx, etc.).
  //
  // Routes that need stronger access pass requiredRole explicitly. Routes
  // without it remain available to every authenticated team member, while
  // the role check below keeps direct URL access aligned with the sidebar.
  requiredRole?: 'admin' | 'manager' | 'member'
}

export const TeamProtectedRoute: React.FC<TeamProtectedRouteProps> = ({ 
  children, 
  teamId, 
  requiredRole = 'member' 
}) => {
  const { user, loading, userTeams } = useAuth()
  const location = useLocation()
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null)
  const [trialLoading, setTrialLoading] = useState(true)
  const [upgrading, setUpgrading] = useState<string | null>(null)
  const [consecutiveErrors, setConsecutiveErrors] = useState(0)

  // Load subscription status to check trial expiration
  useEffect(() => {
    const loadTrialStatus = async () => {
      if (!user || !teamId) return
      
      try {
        const status = await getSubscriptionStatus(teamId, user.id)
        setSubscriptionStatus(status)
        setConsecutiveErrors(0) // Reset error count on success
        // console.log('🔍 Loaded subscription status:', status)
      } catch (error) {
        console.error('Error loading subscription status:', error)
        
        // Handle network errors more gracefully than billing errors
        const isNetworkError = error instanceof Error && 
          (error.name === 'NetworkError' || error.message.includes('Failed to send a request'))
        
        const newErrorCount = consecutiveErrors + 1
        setConsecutiveErrors(newErrorCount)
        
        if (isNetworkError) {
          // For network errors, be more lenient - only fail after 5 attempts
          if (newErrorCount >= 5) {
            console.warn('🚨 Multiple network failures, treating as subscription issue')
            setSubscriptionStatus(null)
          } else {
            // console.log(`⚠️ Network error (${newErrorCount}/5), keeping previous status`)
          }
        } else {
          // For other billing errors, fail after 3 attempts
          if (newErrorCount >= 3) {
            console.warn('🚨 Multiple consecutive billing check failures, treating as subscription issue')
            setSubscriptionStatus(null)
          } else {
            // console.log(`⚠️ Billing check failed (${newErrorCount}/3), keeping previous status`)
          }
        }
      } finally {
        setTrialLoading(false)
      }
    }

    if (user && teamId) {
      loadTrialStatus()
      
      // Set up periodic refresh - reduced frequency to every 2 minutes
      const refreshInterval = setInterval(loadTrialStatus, 120000) // Every 2 minutes (120 seconds)
      
      return () => clearInterval(refreshInterval)
    }
  }, [user, teamId, consecutiveErrors])

  const handleUpgrade = async (planName: string) => {
    if (!user) return

    try {
      setUpgrading(planName)
      const checkoutUrl = await createCheckoutSession(teamId, planName, user.id)
      window.location.href = checkoutUrl
    } catch (error) {
      console.error('Error creating checkout:', error)
      setUpgrading(null)
    }
  }

  if (loading || trialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-2 text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />
  }

  if (!userTeams) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-2 text-muted-foreground">Loading team data...</p>
        </div>
      </div>
    )
  }

  const teamMembership = userTeams.find(membership => membership.team_id === teamId)

  if (!teamMembership) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          {/* No border-radius on this bordered panel, per UI_STYLE_GUIDE.md §1. */}
          <div className="border border-destructive/30 bg-destructive/10 p-6">
            <h2 className="text-lg font-semibold text-destructive mb-2">Access Denied</h2>
            <p className="text-destructive mb-4">You don't have access to this team.</p>
            <Navigate to="/dashboard" replace />
          </div>
        </div>
      </div>
    )
  }

  // Block access for a workspace still awaiting approval, or one that was
  // rejected -- see 20260917120000_add_pending_review_status_to_teams.sql.
  // Existing (pre-feature) teams default to 'approved' so this is a no-op
  // for everyone already using SyncSimple.
  const teamStatus = teamMembership.teams?.status
  if (teamStatus === 'pending_review' || teamStatus === 'rejected') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          {/* No border-radius, per UI_STYLE_GUIDE.md §1. */}
          <div className="border border-border bg-background p-8 max-w-md shadow-sm">
            {teamStatus === 'pending_review' ? (
              <>
                <h2 className="text-lg font-semibold text-foreground mb-2">Your workspace is awaiting approval</h2>
                <p className="text-muted-foreground">
                  We've been notified and someone will review this shortly. This page will update automatically once you're approved — feel free to refresh in a bit.
                </p>
              </>
            ) : (
              <>
                <h2 className="text-lg font-semibold text-foreground mb-2">Access not approved</h2>
                <p className="text-muted-foreground">
                  This workspace's request to use SyncSimple wasn't approved. Reach out if you think this is a mistake.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Check role permissions
  if (requiredRole === 'admin' && teamMembership.role !== 'admin' && teamMembership.role !== 'owner') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          {/* No border-radius on this bordered panel, per UI_STYLE_GUIDE.md §1. */}
          <div className="border border-destructive/30 bg-destructive/10 p-6">
            <h2 className="text-lg font-semibold text-destructive mb-2">Admin Access Required</h2>
            <p className="text-destructive mb-4">You need admin permissions to access this page.</p>
            <Navigate to={`/dashboard/team/${teamId}`} replace />
          </div>
        </div>
      </div>
    )
  }

  if (requiredRole === 'manager' && teamMembership.role !== 'owner' && teamMembership.role !== 'admin' && teamMembership.role !== 'manager') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          {/* No border-radius on this bordered panel, per UI_STYLE_GUIDE.md §1. */}
          <div className="border border-destructive/30 bg-destructive/10 p-6">
            <h2 className="text-lg font-semibold text-destructive mb-2">Manager Access Required</h2>
            <p className="text-destructive mb-4">You need admin or manager permissions to access this page.</p>
            <Navigate to={`/dashboard/team/${teamId}`} replace />
          </div>
        </div>
      </div>
    )
  }

  // Check trial expiration - block all users if trial expired
  if (subscriptionStatus) {
    const trialStatus = checkTrialStatus(subscriptionStatus, subscriptionStatus.team_created_at)
    // console.log('🔍 Trial status check:', trialStatus)
    
    if (trialStatus.isTrialExpired) {
      console.log('🚫 Trial expired, blocking access')
      return (
        <TrialExpiredModal
          isOpen={true}
          memberCount={subscriptionStatus.member_count}
          onUpgrade={handleUpgrade}
          upgrading={upgrading}
        />
      )
    }
  }
  
  // Only block access if we've had multiple consecutive failures
  // This prevents temporary network issues from blocking access
  if (!subscriptionStatus && !trialLoading && consecutiveErrors >= 3) {
    // console.log('🚫 No subscription status available after multiple failures, blocking access')
    return (
      <TrialExpiredModal
        isOpen={true}
        memberCount={0}
        onUpgrade={handleUpgrade}
        upgrading={upgrading}
      />
    )
  }

  return <>{children}</>
} 
